import { memo, useCallback, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@/lib/utils';
import { BoardCardPreview } from '@/components/leads/board/BoardCardPreview';
import type { IBoardCardProps } from '@/@types/leadsBoard';

export const BoardCard = memo(function BoardCard({ lead }: IBoardCardProps) {
  const navigate = useNavigate();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: lead._id,
  });

  // Suppress the click that follows a drag gesture — dnd-kit's activation
  // distance already filters plain clicks, this guards the drag-then-click case.
  const draggedRef = useRef(false);
  useEffect(() => {
    if (isDragging) draggedRef.current = true;
  }, [isDragging]);

  const handleClick = useCallback(() => {
    if (draggedRef.current) {
      draggedRef.current = false;
      return;
    }
    navigate(`/leads/${lead._id}`);
  }, [navigate, lead._id]);

  const style = transform ? { transform: CSS.Translate.toString(transform) } : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-lead-id={lead._id}
      {...attributes}
      {...listeners}
      aria-label={`${lead.name ?? 'Unknown'} card`}
      onClick={handleClick}
      className={cn('cursor-grab active:cursor-grabbing select-none', isDragging && 'opacity-50')}
    >
      <BoardCardPreview
        lead={lead}
        nameSlot={
          <Link
            to={`/leads/${lead._id}`}
            onClick={e => e.stopPropagation()}
            className="block rounded-sm font-medium text-sm text-foreground truncate hover:underline outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
          >
            {lead.name}
          </Link>
        }
      />
    </div>
  );
});
